import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { fmtMoney } from "../lib/api";
import { useAuth } from "../lib/auth";
import { playThwip } from "../lib/sound";
import { getCategoryMeta } from "../lib/categoryIcons";
import { toast } from "sonner";

export default function TransactionDialog({ open, onOpenChange, initial, onSaved }) {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const [type, setType] = useState("expense");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [cats, setCats] = useState({ expense: [], income: [] });
  const [saving, setSaving] = useState(false);
  // "Pay toward" picker — populated when category is a bill/debt category
  const [bills, setBills] = useState([]);
  const [debts, setDebts] = useState([]);
  const [payTarget, setPayTarget] = useState(""); // format: "bill:<id>" | "debt:<id>" | ""

  const BILL_CATEGORIES = ["Utility Bills", "Phone Bill", "Internet"];
  const DEBT_CATEGORIES = ["Credit Cards", "Loans"];
  const isBillCat = BILL_CATEGORIES.includes(category);
  const isDebtCat = DEBT_CATEGORIES.includes(category);
  const showPayTarget = type === "expense" && (isBillCat || isDebtCat);

  useEffect(() => {
    if (open) {
      api.get("/categories").then(r => setCats(r.data));
      // Fetch bills + debts once per open — used only if user picks a payable category
      api.get("/bills").then(r => setBills(r.data?.bills || [])).catch(() => setBills([]));
      api.get("/debts").then(r => setDebts(r.data || [])).catch(() => setDebts([]));
      if (initial) {
        setType(initial.type);
        setAmount(String(initial.amount));
        setCategory(initial.category);
        setDescription(initial.description || "");
        setDate(initial.date ? initial.date.slice(0, 10) : "");
        // Pre-fill current linkage so user can see (and change) which bill/debt this is applied to
        api.get(`/transactions/${initial.id}/pay-target`)
          .then(r => {
            const t = r.data?.pay_target;
            setPayTarget(t ? `${t.kind}:${t.id}` : "");
          })
          .catch(() => setPayTarget(""));
      } else {
        setType("expense"); setAmount(""); setCategory(""); setDescription("");
        setDate(new Date().toISOString().slice(0, 10));
        setPayTarget("");
      }
    }
  }, [open, initial]);

  // When category changes to a non-payable one, clear the target
  useEffect(() => {
    if (!showPayTarget) setPayTarget("");
  }, [showPayTarget]);

  // Filter bills/debts to match the selected category
  const eligibleBills = React.useMemo(() => {
    if (!isBillCat) return [];
    const catMap = { "Utility Bills": ["electric", "gas", "water", "other"], "Phone Bill": ["phone"], "Internet": ["internet"] };
    const allowed = catMap[category] || [];
    return bills.filter(b => allowed.includes(b.category));
  }, [bills, category, isBillCat]);
  const eligibleDebts = React.useMemo(() => {
    if (!isDebtCat) return [];
    const kindMap = { "Credit Cards": "credit_card", "Loans": "loan" };
    const kind = kindMap[category];
    return debts.filter(d => d.type === kind);
  }, [debts, category, isDebtCat]);

  const save = async () => {
    if (!amount || !category) {
      toast.error("Amount and category are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { type, amount: parseFloat(amount), category, description, date: new Date(date).toISOString() };
      // Attach explicit pay target (or a "clear" sentinel) if the picker is visible.
      // On create: only send when a real target is picked (skip "__none__").
      // On edit: always send the user's choice, using {kind:"none",id:""} to clear.
      if (showPayTarget) {
        if (payTarget && payTarget !== "__none__") {
          const [kind, id] = payTarget.split(":");
          if (kind && id) payload.pay_target = { kind, id };
        } else if (initial) {
          // Editing an existing txn — user explicitly picked "None" or left blank; clear any linkage.
          payload.pay_target = { kind: "none", id: "" };
        }
      }
      if (initial) {
        const r = await api.put(`/transactions/${initial.id}`, payload);
        const change = r.data?.pay_target_change;
        if (change?.kind === "debt") {
          toast.success(`Transaction updated · re-linked ${fmtMoney(change.amount, currency)} to ${change.debt_name} (balance ${fmtMoney(change.new_balance, currency)})`);
        } else if (change?.kind === "bill") {
          toast.success(`Transaction updated · re-linked ${fmtMoney(change.amount, currency)} to ${change.bill_name}`);
        } else if (change?.kind === "none") {
          toast.success("Transaction updated · payment no longer linked to a bill or debt");
        } else {
          toast.success("Transaction updated");
        }
      } else {
        const r = await api.post("/transactions", payload);
        const linkedBill = r.data?.linked_bills?.[0];
        const linkedDebt = r.data?.linked_debts?.[0];
        if (linkedDebt) {
          toast.success(`Paid ${fmtMoney(linkedDebt.amount, currency)} on ${linkedDebt.debt_name} · balance now ${fmtMoney(linkedDebt.new_balance, currency)}`);
        } else if (linkedBill) {
          toast.success(`Transaction added · linked ${fmtMoney(linkedBill.amount, currency)} to ${linkedBill.bill_name}`);
        } else {
          toast.success("Transaction added");
        }
      }
      playThwip();
      // Check budget alert
      try {
        const s = await api.get("/analytics/summary");
        const pct = s.data.monthly_budget > 0 ? (s.data.month_expense / s.data.monthly_budget) * 100 : 0;
        if (type === "expense" && pct >= 90) toast.warning(`Heads up — you've used ${Math.round(pct)}% of your monthly budget`);
        else if (type === "expense" && pct >= 75) toast(`You've used ${Math.round(pct)}% of budget`);
      } catch (e) { console.warn("budget check failed", e); }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const list = type === "expense" ? cats.expense : cats.income;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="transaction-dialog" className="rounded-3xl bg-white dark:bg-slate-900 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">{initial ? "Edit Transaction" : "New Transaction"}</DialogTitle>
        </DialogHeader>

        <div className="flex bg-slate-100 dark:bg-slate-800 rounded-full p-1 my-2">
          <button
            data-testid="type-expense-btn"
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${type === "expense" ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400"}`}
            onClick={() => setType("expense")}>Expense</button>
          <button
            data-testid="type-income-btn"
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${type === "income" ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400"}`}
            onClick={() => setType("income")}>Income</button>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Amount</Label>
            <Input data-testid="tx-amount-input" type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className="rounded-xl mt-1.5" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="tx-category-select" className="rounded-xl mt-1.5"><SelectValue placeholder="Choose category" /></SelectTrigger>
              <SelectContent>{list.map(c => {
                const meta = getCategoryMeta(c);
                const Ico = meta.Icon;
                return (
                  <SelectItem key={c} value={c}>
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-lg grid place-items-center ${meta.bg} ${meta.fg}`}>
                        <Ico className="w-3.5 h-3.5" />
                      </span>
                      {c}
                    </div>
                  </SelectItem>
                );
              })}</SelectContent>
            </Select>
          </div>

          {/* Pay toward specific bill or debt */}
          {showPayTarget && (eligibleBills.length > 0 || eligibleDebts.length > 0) && (
            <div data-testid="pay-target-section">
              <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">
                Pay toward {isBillCat ? "a bill" : isDebtCat ? "a card / loan" : ""}
              </Label>
              <Select value={payTarget} onValueChange={setPayTarget}>
                <SelectTrigger data-testid="pay-target-select" className="rounded-xl mt-1.5">
                  <SelectValue placeholder={`Optional — pick a ${isBillCat ? "bill" : "debt"} to apply this payment to`} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="pay-target-none">
                    <span className="text-slate-500 dark:text-slate-400">— None (just log the transaction)</span>
                  </SelectItem>
                  {eligibleBills.map(b => (
                    <SelectItem key={`bill-${b.id}`} value={`bill:${b.id}`} data-testid={`pay-target-bill-${b.id}`}>
                      <div className="flex items-center justify-between gap-3 w-full">
                        <span className="font-medium">{b.name}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400 tabular">
                          {fmtMoney(b.paid_this_month?.remaining ?? b.amount, currency)} left
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                  {eligibleDebts.map(d => (
                    <SelectItem key={`debt-${d.id}`} value={`debt:${d.id}`} data-testid={`pay-target-debt-${d.id}`}>
                      <div className="flex items-center justify-between gap-3 w-full">
                        <span className="font-medium">{d.name}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400 tabular">
                          {fmtMoney(d.balance, currency)} balance
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {payTarget && payTarget !== "__none__" && (
                <div className="mt-1.5 text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                  ✓ This payment will {payTarget.startsWith("debt:") ? "reduce the debt balance" : "count toward the bill this month"}
                </div>
              )}
            </div>
          )}
          <div>
            <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Date</Label>
            <Input data-testid="tx-date-input" type="date" value={date} onChange={e => setDate(e.target.value)} className="rounded-xl mt-1.5" />
          </div>
          <div>
            <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Description (optional)</Label>
            <Input data-testid="tx-desc-input" value={description} onChange={e => setDescription(e.target.value)} className="rounded-xl mt-1.5" />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="tx-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-spidey-red text-white hover:opacity-90" data-testid="tx-save-btn">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
